import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { instructionsApi } from 'src/Services/API';
import { CreateInstructionDto, InstructionDto } from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';

type UpdateInstructionParams = {
  instructionId: string;
  updateData: CreateInstructionDto;
};

export const useUpdateInstruction = (
  options?: UseMutationOptions<InstructionDto, unknown, UpdateInstructionParams>
) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();

  return useMutation<InstructionDto, unknown, UpdateInstructionParams>(
    ['update-instruction', assetId],
    async ({ instructionId, updateData }: UpdateInstructionParams) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }

      const response = await instructionsApi.instructionsControllerUpdate(
        assetId,
        instructionId,
        updateData
      );
      return response.data;
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        // Invalidate relevant queries to refresh data
        queryClient.invalidateQueries(['fetch-instructions', assetId]);
        queryClient.invalidateQueries(['fetch-instruction', assetId, variables.instructionId]);
        queryClient.invalidateQueries('fetch-documents');
        options?.onSuccess?.(data, variables, context);
        trackEvent('Instruction', 'update', data.title);
      },
    }
  );
};

export default useUpdateInstruction;
